import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';

export interface EventPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChangePage: (page: number) => void;
}

export const EventPagination = ({
  page,
  pageSize,
  total,
  onChangePage,
}: EventPaginationProps): ReactElement => {
  const numPages = Math.ceil(total / pageSize);
  const isFirst = page <= 1;
  const isLast = page >= numPages;

  const buttonBase = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    px: '8px',
    py: '10px',
    border: '1px solid #d1d5dc',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    userSelect: 'none' as const,
  };

  return (
    <Box display="flex" alignItems="center" gap="24px">
      {/* Previous button */}
      <Box
        component="button"
        onClick={() => !isFirst && onChangePage(page - 1)}
        disabled={isFirst}
        sx={{
          ...buttonBase,
          opacity: isFirst ? 0.5 : 1,
          cursor: isFirst ? 'default' : 'pointer',
          '&:hover': { backgroundColor: isFirst ? '#ffffff' : '#f9fafb' },
        }}
      >
        <ChevronLeftRounded sx={{ fontSize: 16, color: '#383838' }} />
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: pxToEm(14),
            fontWeight: 400,
            color: '#383838',
            lineHeight: '21px',
          }}
        >
          Previous
        </Typography>
      </Box>

      {/* Page indicator */}
      <Box display="flex" alignItems="center" gap="8px">
        {['Page', String(page), 'of', String(numPages)].map((word, i) => (
          <Typography
            key={i}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: pxToEm(14),
              fontWeight: 400,
              color: '#383838',
              lineHeight: '21px',
            }}
          >
            {word}
          </Typography>
        ))}
      </Box>

      {/* Next button */}
      <Box
        component="button"
        onClick={() => !isLast && onChangePage(page + 1)}
        disabled={isLast}
        sx={{
          ...buttonBase,
          minWidth: '99px',
          justifyContent: 'center',
          opacity: isLast ? 0.5 : 1,
          cursor: isLast ? 'default' : 'pointer',
          '&:hover': { backgroundColor: isLast ? '#ffffff' : '#f9fafb' },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: pxToEm(14),
            fontWeight: 400,
            color: '#383838',
            lineHeight: '21px',
          }}
        >
          Next
        </Typography>
        <ChevronRightRounded sx={{ fontSize: 16, color: '#383838' }} />
      </Box>
    </Box>
  );
};
